import React, { useEffect, useState, useRef } from 'react';
import { MessageSquareIcon, SendIcon, XIcon, MinimizeIcon, MaximizeIcon, BotIcon, UserIcon, SearchIcon, ThumbsUpIcon, ThumbsDownIcon, ClipboardIcon } from 'lucide-react';
import { Button } from '../ui/Button';
interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}
export function ExpenseAIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    content: "Hello! I'm your AI expense assistant. I can help you with expense policies, per diem rates, and guide you through submitting expenses. How can I help you today?",
    role: 'assistant',
    timestamp: new Date()
  }]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages]);
  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);
  // Handle sending a message
  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      role: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    // Simulate AI response (would connect to actual AI service in production)
    setTimeout(() => {
      const aiResponse = generateAIResponse(inputValue);
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponse,
        role: 'assistant',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsLoading(false);
    }, 1500);
  };
  // Handle keyboard submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  // Generate AI response based on user input (simplified mock)
  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    if (input.includes('per diem') || input.includes('allowance')) {
      return 'Per diem rates vary by location. For Washington DC, the current rates are: $76 for lodging and $64 for meals and incidentals (M&IE). The meal breakdown is: $16 for breakfast, $17 for lunch, $26 for dinner, and $5 for incidentals. Would you like me to check rates for another location?';
    }
    if (input.includes('receipt') || input.includes('receipts')) {
      return 'Receipts are required for all expenses over $75. For meal expenses, you need to list all attendees and the business purpose. You can upload receipts by taking a photo or attaching a file during expense submission.';
    }
    if (input.includes('submit') || input.includes('create') || input.includes('new expense')) {
      return "To submit a new expense, go to the 'New Expense' page from the sidebar menu. Fill out all required fields, attach any necessary receipts, and click 'Submit Expense'. Your submission will be sent to your manager for approval.";
    }
    if (input.includes('policy') || input.includes('policies')) {
      return 'Our expense policy follows GSA guidelines for per diem rates. Business travel requires pre-approval. Expenses must be submitted within 30 days of incurring them. For detailed policy information, check the Knowledge Base section.';
    }
    if (input.includes('approval') || input.includes('status')) {
      return "You can check the status of your expense submissions on your dashboard. Approvals typically take 2-3 business days. If an expense is rejected, you'll receive a notification with the reason.";
    }
    return "I'm not sure I understand your question. Could you rephrase it? I can help with expense policies, per diem rates, submission procedures, and checking the status of your expenses.";
  };
  // Handle feedback
  const handleFeedback = (isPositive: boolean, messageId: string) => {
    // In a real implementation, this would send feedback to improve the AI
    console.log(`Feedback for message ${messageId}: ${isPositive ? 'positive' : 'negative'}`);
    // Show a thank you message
    alert('Thank you for your feedback! This helps improve the AI assistant.');
  };
  // Copy message to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };
  if (!isOpen) {
    return <Button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg flex items-center justify-center" aria-label="Open AI Assistant" data-id="element-82">
        <MessageSquareIcon size={24} data-id="element-83" />
      </Button>;
  }
  return <div className={`fixed right-6 ${isMinimized ? 'bottom-6' : 'bottom-0'} z-30 flex flex-col ${isMinimized ? 'h-14 w-64 rounded-full' : 'h-[550px] w-80 sm:w-96 rounded-t-lg'} bg-gray-800 shadow-xl transition-all duration-200 border border-gray-700 overflow-hidden`} data-id="element-84">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-750 border-b border-gray-700 cursor-pointer" onClick={() => isMinimized ? setIsMinimized(false) : null} data-id="element-85">
        <div className="flex items-center" data-id="element-86">
          <BotIcon size={20} className="text-blue-400 mr-2" data-id="element-87" />
          {!isMinimized && <h3 className="font-medium text-gray-100" data-id="element-88">AI Expense Assistant</h3>}
          {isMinimized && <h3 className="font-medium text-gray-100" data-id="element-89">AI Assistant</h3>}
        </div>
        <div className="flex items-center space-x-1" data-id="element-90">
          {!isMinimized && <button onClick={e => {
          e.stopPropagation();
          setIsMinimized(true);
        }} className="p-1 text-gray-400 hover:text-gray-300 rounded" aria-label="Minimize" data-id="element-91">
              <MinimizeIcon size={16} data-id="element-92" />
            </button>}
          {isMinimized && <button onClick={e => {
          e.stopPropagation();
          setIsMinimized(false);
        }} className="p-1 text-gray-400 hover:text-gray-300 rounded" aria-label="Maximize" data-id="element-93">
              <MaximizeIcon size={16} data-id="element-94" />
            </button>}
          <button onClick={e => {
          e.stopPropagation();
          setIsOpen(false);
        }} className="p-1 text-gray-400 hover:text-gray-300 rounded" aria-label="Close" data-id="element-95">
            <XIcon size={16} data-id="element-96" />
          </button>
        </div>
      </div>
      {!isMinimized && <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-97">
            {messages.map(message => <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-98">
                <div className={`flex max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-99">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${message.role === 'user' ? 'bg-blue-600 ml-2' : 'bg-gray-700 mr-2'}`} data-id="element-100">
                    {message.role === 'user' ? <UserIcon size={16} className="text-blue-100" data-id="element-101" /> : <BotIcon size={16} className="text-gray-300" data-id="element-102" />}
                  </div>
                  <div className="relative group" data-id="element-103">
                    <div className={`p-3 rounded-lg ${message.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200'}`} data-id="element-104">
                      <p className="text-sm whitespace-pre-wrap" data-id="element-105">
                        {message.content}
                      </p>
                      <span className="text-xs opacity-70 mt-1 block" data-id="element-106">
                        {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </span>
                    </div>
                    {/* Action buttons - only show for AI messages */}
                    {message.role === 'assistant' && <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-107">
                        <div className="flex space-x-1" data-id="element-108">
                          <button onClick={() => copyToClipboard(message.content)} className="p-1 bg-gray-800 rounded hover:bg-gray-700 text-gray-300" title="Copy to clipboard" data-id="element-109">
                            <ClipboardIcon size={12} data-id="element-110" />
                          </button>
                          <button onClick={() => handleFeedback(true, message.id)} className="p-1 bg-gray-800 rounded hover:bg-gray-700 text-gray-300" title="This was helpful" data-id="element-111">
                            <ThumbsUpIcon size={12} data-id="element-112" />
                          </button>
                          <button onClick={() => handleFeedback(false, message.id)} className="p-1 bg-gray-800 rounded hover:bg-gray-700 text-gray-300" title="This was not helpful" data-id="element-113">
                            <ThumbsDownIcon size={12} data-id="element-114" />
                          </button>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>)}
            {/* Loading indicator */}
            {isLoading && <div className="flex justify-start" data-id="element-115">
                <div className="flex max-w-[85%]" data-id="element-116">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-700 mr-2 flex items-center justify-center" data-id="element-117">
                    <BotIcon size={16} className="text-gray-300" data-id="element-118" />
                  </div>
                  <div className="p-3 rounded-lg bg-gray-700 text-gray-200 flex items-center space-x-2" data-id="element-119">
                    <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-120"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-121"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-122"></div>
                  </div>
                </div>
              </div>}
            {/* Auto-scroll anchor */}
            <div ref={messagesEndRef} data-id="element-123" />
          </div>
          {/* Quick suggestions */}
          <div className="px-4 py-2 border-t border-gray-700" data-id="element-124">
            <div className="flex flex-wrap gap-2 mb-2" data-id="element-125">
              <button onClick={() => setInputValue('What are the per diem rates for New York?')} className="text-xs bg-gray-700 hover:bg-gray-650 text-gray-300 px-3 py-1.5 rounded-full" data-id="element-126">
                Per diem rates
              </button>
              <button onClick={() => setInputValue('Do I need receipts for all expenses?')} className="text-xs bg-gray-700 hover:bg-gray-650 text-gray-300 px-3 py-1.5 rounded-full" data-id="element-127">
                Receipt requirements
              </button>
              <button onClick={() => setInputValue('How do I submit a new expense?')} className="text-xs bg-gray-700 hover:bg-gray-650 text-gray-300 px-3 py-1.5 rounded-full" data-id="element-128">
                Submit expense
              </button>
            </div>
          </div>
          {/* Input */}
          <div className="p-3 border-t border-gray-700 bg-gray-750" data-id="element-129">
            <div className="relative" data-id="element-130">
              <textarea ref={inputRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask a question about expenses..." className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 placeholder-gray-400 resize-none text-sm" rows={1} style={{
            maxHeight: '120px',
            minHeight: '40px'
          }} data-id="element-131" />
              <button onClick={handleSendMessage} disabled={inputValue.trim() === '' || isLoading} className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 rounded-full ${inputValue.trim() === '' || isLoading ? 'text-gray-500 cursor-not-allowed' : 'text-blue-500 hover:text-blue-400 hover:bg-gray-600'}`} aria-label="Send message" data-id="element-132">
                <SendIcon size={16} data-id="element-133" />
              </button>
            </div>
          </div>
        </>}
    </div>;
}